import Feature from '../../components/form/Feature';
import { formStructure, pageStructure, attachmentStructure } from '../../components/form/structure';
import { strings, constants } from '../../utils';
import timeout from '../../utils/timeout';
import api from '../../api';
import featureList from '../../components/builder/featureList';
const parentFeatureTypes = Object.keys(featureList.childFeatures);
const cloneDeep = require('lodash/cloneDeep');
const debounce = require('lodash/debounce');
const merge = require('lodash/merge');
const dateFormat = require('dateformat');

const UNDO_REDO_MAX_STACK = 10;
const UNDO_REDO_WAIT = 500;
const UNDO_REDO_MAX_WAIT = 5000;

export default {
    namespaced: true,
    state: {
        hasChanges: false,
        pendingPath: '',
        pendingChanges: {},
        pendingName: null,
        pendingFeatures: null,
        activeFeatures: [],
        copiedFeatures: [],
        propertiesDialog: {},
        forms: [formStructure()],
        sessionStarted: null,
        tempFeature: null,
        currentState: null,
        undos: [],
        redos: [],
    },
    getters: {
        state(state) {
            return state;
        },
        forms(state) {
            return state.forms;
        },
        form: (state) => index => {
            return state.forms[index];
        },
        formById: state => id => {
            return state.forms.find(form => form.id == id);
        },
        pages(state) {
            return state.forms.reduce((acc, form) => {
                return acc.concat(form.data.pages);
            }, []);
        },
        page: (state, getters) => index => {
            return getters.pages[index];
        },
        features(state, getters) {
            return getters.pages.reduce((acc, page) => {
                return acc.concat(page.features);
            }, []);
        },
        featureById: (state, getters) => id => {
            return getters.features.find(feature => feature.id === id);
        },
        featuresByGroupId: (state, getters) => (groupId, skipFeatureId = null) => {
            return getters.features.filter(f => {
                return f.groupId === groupId && (!skipFeatureId || f.id !== skipFeatureId);
            });
        },
        featuresByForm: (state, getters) => formId => {
            let pages = getters.formById(formId).data.pages;
            return pages.reduce((acc, page) => {
                return acc.concat(page.features);
            }, [])
        },
        featuresByType: (state, getters) => type => {
            return getters.features.filter(f => f.type === type);
        },
        propertiesDialog(state) {
            return state.propertiesDialog;
        },
        tempFeature(state) {
            return state.tempFeature;
        },
        numForms(state) {
            return state.forms.length;
        },
        numPages(state, getters) {
            return getters.pages.length;
        },
        numBackgroundImages(state, getters) {
            return getters
                .pages
                .filter(page => page.backgroundImage)
                .length;
        },
        backgroundImages(state, getters) {
            return getters.pages.map(page => page.backgroundImage)
        },
        formPageIndexes(state) {
            let indexes = {};
            let index = 0;
            for (let form of state.forms) {
                indexes[form.id] = [];
                for (let page of form.data.pages) {
                    indexes[form.id].push(index++);
                }
            }
            return indexes;
        },
        pageAbsY: (state, getters, rootState, rootGetters) => index => {
            let y = 0;
            let scaleFactor = rootGetters['layout/scaleFactor'];
            let measurements = getters.pageMeasurements;

            for (let i = 0; i <= index; i++) {
                let prevPageHeight = i === 0 ? 0 : measurements(i - 1).height + measurements(i - 1).marginBottom / scaleFactor;
                y += prevPageHeight + measurements(i).marginTopScaled / scaleFactor;
            };

            return y;
        },
        pageAbsX: (state, getters) => index => {
            return getters.pageMeasurements(index).left;
        },
        pageFormIndex: (state) => page => { // returns index of form that contains given page
            let isIndex = typeof page === 'number';
            let pageCount = 0;
            return state.forms.findIndex(form => {
                if (isIndex) {
                    pageCount += form.data.pages.length;
                    return page < pageCount;
                } else {
                    return form.data.pages.some(p => p === page);
                }
            });
        },
        featurePageIndex: (state, getters) => feature => { // returns index of page that contains given feature
            return getters.pages.findIndex(page => page.features.some(f => f === feature));
        },
        largestPageW(state, getters) {
            return (
                getters.pages.reduce((acc, page, i) => {
                    let width = getters.pageMeasurementsBasic(i).width;
                    return width > acc ? width : acc;
                }, 0)
            );
        },
        largestPageH(state, getters) {
            return (
                getters.pages.reduce((acc, page, i) => {
                    let height = getters.pageMeasurementsBasic(i).height;
                    return height > acc ? height : acc;
                }, 0)
            );
        },
        maxTabIndex(state, getters) {
            if (!getters.features.length) return 0;

            return getters.features.reduce((acc, feature) => {
                if (!acc.tabIndex || feature.tabIndex > acc.tabIndex) return feature;

                return acc
            }, getters.features[0]).tabIndex || 0;
        },
        activeFeatures(state) {
            return state.activeFeatures;
        },
        copiedFeatures(state) {
            return state.copiedFeatures;
        },
        userScaleFactor(state) {
            return state.userScaleFactor;
        },
        isActiveFeature: state => feature => {
            return state.activeFeatures.includes(feature);
        },
        pendingPath(state) {
            return state.pendingPath;
        },
        pendingChanges(state) {
            return state.pendingChanges;
        },
        pendingName(state) {
            return state.pendingName
        },
        pendingFeatures(state) {
            return state.pendingFeatures
        },
        sessionStarted(state) {
            return state.sessionStarted;
        },
        featurePositions: (state, getters) => features => {
            return features.map(feature => {
                let props = {};
                ['x', 'y', 'width', 'height'].forEach(prop => {
                    let val = feature[prop];
                    if (prop === 'width' || prop === 'height') {
                        val = Math.max(constants.FORM_PPMM, val);
                    }
                    props[prop] = val;
                });
                return props;
            });
        },
        featurePositionsScaled: (state, getters, rootState, rootGetters) => features => {
            let scaleFactor = rootGetters['layout/scaleFactor'];
            let positions = getters.featurePositions(features);
            return positions.map(pos => {
                for (let key in pos) {
                    pos[key] = Math.round(pos[key] * scaleFactor);
                }
                return pos;
            });
        },
        activeFeaturePositionsScaled(state, getters, rootState, rootGetters) {
            let pending = getters.pendingChanges;
            let scaleFactor = rootGetters['layout/scaleFactor'];
            return getters.activeFeatures.map(feature => {
                let props = {};
                ['x', 'y', 'width', 'height'].forEach(prop => {
                    let val = feature[prop];
                    if (pending[prop]) val += pending[prop];
                    if (prop === 'width' || prop === 'height') {
                        val = Math.max(constants.FORM_PPMM, val);
                    }
                    props[prop] = Math.round(val * scaleFactor);
                });
                return props;
            });
        },
        featureNameIsUnique: (state, getters) => name => {
            if (!name) return false;
            let form = getters.form(0);
            for (let page of form.data.pages) {
                for (let feature of page.features) {
                    if (feature.name === name) {
                        return false;
                    }
                }
            }
            return true;
        },
        copyPageSettings: (state, getters) => index => {
            const page = getters.page(index);
            const settings = {};
            const copyProps = [
                'pageWidth',
                'pageHeight',
                'pageSize',
                'orientation'
            ];
            for (let prop of copyProps) {
                if (page[prop] !== null) {
                    settings[prop] = page[prop];
                }
            }
            return settings;
        },
        pageMeasurements: (state, getters) => index => {
            let measurements = getters.pageMeasurementsBasic(index);
            let measurementsScaled = getters._pageMeasurementsScaled(measurements);
            let lefts = getters._pageLefts(measurementsScaled.widthScaled);
            return { ...measurements, ...measurementsScaled, ...lefts }
        },
        pageMeasurementsBasic: (state, getters) => index => {
            let page = getters.page(index);
            let form = state.forms[getters.pageFormIndex(page)];
            return getters.staticFormMeasurements(form, page, index)
        },
        staticFormMeasurements: (state, getters, rootState, rootGetters) => (form, page, index) => {
            let padding = rootGetters['layout/padding'];
            let height;
            let width;

            // get measurements from page, or inherit from form
            let pageSize = '', orientation = '';
            if (!page) {
                // Use US letter size for attachment width if no forms present
                pageSize = 'letter';
                let size = constants.PAGE_SIZES[pageSize];
                height = size.height;
                width = size.width;
                orientation = 'portrait';
            } else if (page.pageWidth && page.pageHeight) {
                height = page.pageHeight;
                width = page.pageWidth;
            } else if (page.pageSize) {
                pageSize = page.pageSize;
                let size = constants.PAGE_SIZES[pageSize.toLowerCase()];
                height = size.height;
                width = size.width;
                orientation = page.orientation || form.data.orientation;
            } else if (form.data.pageWidth && form.data.pageHeight) {
                height = form.data.pageHeight;
                width = form.data.pageWidth;
            } else {
                pageSize = form.data.pageSize;
                let size = constants.PAGE_SIZES[pageSize.toLowerCase()];
                height = size.height;
                width = size.width;
                orientation = page.orientation || form.data.orientation;
            }

            if (orientation.toLowerCase() === 'landscape') {
                let tempHeight = height;
                height = width;
                width = tempHeight;
            }

            return {
                orientation,
                pageSize,
                width,
                height,
                marginTop: index === 0 ? padding : 0,
                marginBottom: padding * 2,
            };
        },
        _pageMeasurementsScaled: (state, getters, rootState, rootGetters) => (measurements) => {
            let scaleFactor = rootGetters['layout/scaleFactor'];
            let measurementsScaled = {};

            for (let key in measurements) {
                let val = measurements[key];
                if (!isNaN(val)) measurementsScaled[key + 'Scaled'] = val * scaleFactor;
            }

            return measurementsScaled;
        },
        largestPageMeasurementsScaled(state, getters, rootState, rootGetters) {
            let dimensions = rootGetters['layout/dimensions'];
            let scaleFactor = rootGetters['layout/scaleFactor'];
            let widthScaled = getters.largestPageW * scaleFactor;
            let leftScaled = Math.max((dimensions.canvasWidth - widthScaled) / 2, 0);

            return { widthScaled, leftScaled }
        },
        _pageLefts: (state, getters, rootState, rootGetters) => widthScaled => {
            let scaleFactor = rootGetters['layout/scaleFactor'];
            let largestPage = getters.largestPageMeasurementsScaled;
            let scaledLeft = largestPage.leftScaled;

            if (widthScaled !== largestPage.widthScaled) {
                let diffW = largestPage.widthScaled - widthScaled;
                scaledLeft = scaledLeft + diffW / 2;
            }

            return {
                scaledLeft,
                left: scaledLeft / scaleFactor
            }
        },
        currentState(state) {
            return state.currentState;
        },
        undos(state) {
            return state.undos
        },
        redos(state) {
            return state.redos;
        },
        hasChanges(state) {
            return state.hasChanges;
        },
        signatureFeatures(state, getters) {
            return getters.features.filter(f => f.type === 'signature');
        },
        featureGroups(state) {
            if (state.forms.length === 1) {
                return state.forms[0].data.featureGroups;
            }
            let groups = {};
            for (let form of state.forms) {
                merge(groups, form.data.featureGroups);
            }
            return groups;
        },
        featureGroupsByType(state, getters) {
            let groups = {};
            for (let id in getters.featureGroups) {
                let group = getters.featureGroups[id];
                if (!groups[group.type]) {
                    groups[group.type] = [];
                }
                groups[group.type].push(group);
            }
            return groups;
        },
        featureGroupsByFormId: (state) => (formId) => {
            for (let form of state.forms) {
                if (form.id === formId) {
                    return form.data.featureGroups;
                }
            }
        },
        activeFeatureGroupId(state, getters) {
            let groupId = null;
            for (let feature of getters.activeFeatures) {
                if (
                    !feature.groupId ||
                    (groupId && groupId !== feature.groupId)
                ) {
                    return null;
                }
                groupId = feature.groupId;
            }
            return groupId;
        },
        activeFeatureGroup(state, getters) {
            return getters.activeFeatureGroupId ? getters.featureGroups[getters.activeFeatureGroupId] : null;
        },
        newFeatureName: (state, getters) => (feature) => {
            let name = feature.name.replace(/\s/g, '_').replace(/\W/g, '');
            if (!name) {
                name = feature.typeName + '_' + feature.id;
            } else if (!/[a-z]/i.test(name)) {
                name = feature.typeName + '_' + name;
            }
            while (!getters.featureNameIsUnique(name)) {
                let match = name.match(/_([0-9]+)$/);
                if (match) {
                    name = name.substring(0, name.length - match[1].length) + (parseInt(match[1]) + 1);
                } else {
                    name = name + '_1';
                }
            }
            return name;
        },
    },
    mutations: {
        state(state, newState) {
            for (let key in newState) {
                state[key] = cloneDeep(newState[key]);
            }
        },
        currentState(state, currentState) {
            state.currentState = cloneDeep(currentState);
        },
        undos(state, undos) {
            state.undos = undos
        },
        redos(state, redos) {
            state.redos = redos;
        },
        activeFeatures(state, features) {
            state.activeFeatures = features;
        },
        toggleActiveFeature(state, feature) {
            let index = state.activeFeatures.indexOf(feature);
            if (index === -1) {
                state.activeFeatures.push(feature);
            } else {
                state.activeFeatures.splice(index, 1);
            }
        },
        addFeature(state, { feature, pageIndex }) {
            state.forms[0].data.pages[pageIndex].features.push(feature);
        },
        removeFeature(state, { feature, pageIndex }) {
            let featureIndex = state.forms[0].data.pages[pageIndex].features.indexOf(feature);
            if (featureIndex !== -1) state.forms[0].data.pages[pageIndex].features.splice(featureIndex, 1);
        },
        deleteFeatures(state) {
            state.forms[0].data.pages.forEach(page => {
                state.activeFeatures.forEach(feature => {
                    let featureIndex = page.features.indexOf(feature);
                    if (featureIndex !== -1) page.features.splice(featureIndex, 1);
                });
            });
            state.activeFeatures = [];
        },
        copyFeatures(state) {
            state.copiedFeatures = cloneDeep(state.activeFeatures);
        },
        updateFeature(state, { feature, updates }) {
            for (let key in updates) {
                feature[key] = updates[key];
            }
        },
        updateFormName(state, name) {
            state.forms[0].name = name;
        },
        updateFormSettings(state, settings) {
            for (let key in settings) {
                state.forms[0].data.settings[key] = settings[key];
            }
        },
        isDraft(state, isDraft) {
            state.forms[0].is_draft = isDraft;
        },
        startSession(state, override = false) {
            if (override || state.sessionStarted === null) {
                state.sessionStarted = Date.now();
            }
        },
        saveAsCopy(state) {
            state.forms[0].id = null;
            state.forms[0].name += ' (copy)';
        },
        setForms(state, forms) {
            let structured = [];
            for (let form of forms) {
                structured.push(formStructure(form));
            }
            state.forms = structured;
            state.activeFeatures = [];
            state.copiedFeatures = [];
            state.pendingChanges = {};
            state.sessionStarted = Date.now();
        },
        resetForms(state) {
            state.activeFeatures = [];
            state.forms = [formStructure()];
            state.sessionStarted = null;
        },
        setPendingPath(state, path) {
            state.pendingPath = path;
        },
        setPendingChanges(state, pending) {
            state.pendingChanges = pending;
        },
        incrementPendingChanges(state, changes) {
            for (let key in changes) {
                if (state.pendingChanges[key] === undefined) {
                    // Create a new object to trigger reactive updates for this new prop
                    state.pendingChanges = {
                        ...state.pendingChanges,
                        [key]: changes[key],
                    };
                } else {
                    state.pendingChanges[key] += changes[key];
                }
            }
        },
        addPage(state, { page, index }) {
            if (index === undefined) index = state.forms[0].data.pages.length;
            if (page === undefined) page = {};
            const newPage = pageStructure(page);
            state.forms[0].data.pages.splice(index, 0, newPage);
        },
        movePage(state, { index, newIndex }) {
            const page = state.forms[0].data.pages.splice(index, 1)[0];
            state.forms[0].data.pages.splice(newIndex, 0, page);
        },
        deletePage(state, index) {
            const page = state.forms[0].data.pages[index];
            for (let feature of page.features) {
                let i = state.activeFeatures.indexOf(feature);
                if (i >= 0) state.activeFeatures.splice(i, 1);
            }
            state.forms[0].data.pages.splice(index, 1);
            if (!state.forms[0].data.pages.length) {
                state.forms[0].data.pages.push(pageStructure());
            }
        },
        updatePage(state, { index, settings }) {
            const page = state.forms[0].data.pages[index];
            for (let key in settings) {
                page[key] = settings[key];
            }
        },
        changeFeatureOrder(state, { pageIndex, oldFeatureIndex, newFeatureIndex }) {
            const page = state.forms[0].data.pages[pageIndex];
            const feature = page.features.splice(oldFeatureIndex, 1);
            page.features.splice(newFeatureIndex, 0, feature[0]);
        },
        propertiesDialog(state, props) {
            state.propertiesDialog = props;
        },
        tempFeature(state, { type, preset, x, y, onClear, groupId, dataElementDefinitionId }) {
            state.tempFeature = Feature.build({ type, preset, x, y, groupId, dataElementDefinitionId });
            state.tempFeature.onClear = onClear;
        },
        updateTempFeature(state, updates) {
            for (let key in updates) {
                state.tempFeature[key] = updates[key];
            }
        },
        clearTempFeature(state) {
            state.tempFeature.onClear();
            state.tempFeature = null;
        },
        hasChanges(state, hasChanges) {
            state.hasChanges = hasChanges;
        },
        addAttachment(state, index) {
            if (index === undefined) index = state.forms[0].data.attachments.length;
            state.forms[0].data.attachments.splice(index, 0, attachmentStructure());
        },
        moveAttachment(state, { index, newIndex }) {
            const attachment = state.forms[0].data.attachments.splice(index, 1)[0];
            state.forms[0].data.attachments.splice(newIndex, 0, attachment);
        },
        deleteAttachment(state, index) {
            state.forms[0].data.attachments.splice(index, 1);
        },
        updateAttachment(state, { index, data }) {
            for (let key in data) {
                state.forms[0].data.attachments[index][key] = data[key];
                if (key === 'files') {
                    state.forms[0].data.attachments[index].is_filler_attachment = data[key].length === 0
                }
            }
        },
        addDataElement(state, dataElement) {
            state.forms[0].data.dataElements.push(dataElement);
        },
        deleteDataElement(state, featureId) {
            let index = state.forms[0].data.dataElements.findIndex(def => def.featureId === featureId);

            if (index !== -1) {
                state.forms[0].data.dataElements.splice(index, 1);
            }
        },
        textOverlays(state, { pageIndex, textOverlays }) {
            state.forms[0].data.pages[pageIndex].textOverlays = textOverlays;
        },
        featureGroups(state, groups) {
            state.forms[0].data.featureGroups = groups;
        },
        updateFeatureGroup(state, { groupId, updates }) {
            let group = state.forms[0].data.featureGroups[groupId];
            for (let key in updates) {
                group[key] = updates[key];
            }
        },
        setFeatureFlag(state, { flag, value }) {
            state.forms[0].data.featureFlags[flag] = value;
        },
        setPendingName(state, name) {
            state.pendingName = name
        },
        setPendingFeatures(state, features) {
            state.pendingFeatures = features
        },
    },
    actions: {
        onMutation({ dispatch, commit, getters }, { type, payload }) {
            // Update the hasChanges flag if certain mutations occur
            let formChangeTriggers = [
                'updateFormName',
                'changeFeatureOrder',
                'addFeature',
                'updateFeature',
                'deleteFeatures',
                'addPage',
                'updatePage',
                'movePage',
                'deletePage',
            ];
            if (getters.currentState && !getters.hasChanges && formChangeTriggers.includes(type)) {
                commit('hasChanges', true);
            }

            // Add an undo state if certain mutations occur
            let pushStateTriggers = [
                'setForms',
                'resetForms',
                'addFeature',
                'updateFeature',
                'changeFeatureOrder',
                'deleteFeatures',
                'addPage',
                'updatePage',
                'movePage',
                'deletePage',
            ];
            if (pushStateTriggers.includes(type)) {
                // specific payloads to be ignored, if present within trigger mutations
                let ignoreIfPresent = [];
                let ignoredKeyPresent = false;
                ignoreIfPresent.forEach(str => {
                    let arr = str.split('/');
                    if (type !== arr[0]) return;

                    let match = arr.slice(1).reduce((acc, key) => {
                        if (acc && acc[key]) {
                            acc = acc[key];
                        } else {
                            acc = undefined;
                        }

                        return acc;
                    }, payload);

                    if (match !== undefined) ignoredKeyPresent = true;
                });
                if (!ignoredKeyPresent) {
                    dispatch('pushState');
                }
            }
        },
        async undo({ commit, getters, dispatch }) {
            if (!getters.undos.length) return;

            dispatch('push', { name: 'redos', item: getters.currentState });
            let item = await dispatch('pop', { name: 'undos' });
            commit('currentState', item)
            commit('state', item)
        },
        async redo({ commit, getters, dispatch }) {
            if (!getters.redos.length) return;

            dispatch('push', { name: 'undos', item: getters.currentState })
            let item = await dispatch('pop', { name: 'redos' });
            commit('currentState', item)
            commit('state', item)
        },
        pushState: debounce(async ({ commit, getters, dispatch }) => {
            if (getters.currentState) {
                await dispatch('push', { name: 'undos', item: cloneDeep(getters.currentState) })
            }

            commit('redos', []);
            commit('currentState', {
                activeFeatures: getters.activeFeatures,
                forms: getters.forms
            });
        }, UNDO_REDO_WAIT, { maxWait: UNDO_REDO_MAX_WAIT }),
        pop({ getters, commit }, { name }) {
            if (!['undos', 'redos'].includes(name)) {
                throw Error('Invalid value ' + name);
            }

            let item = getters[name][getters[name].length - 1];
            commit(name, getters[name].slice(0, getters[name].length - 1));

            return item;
        },
        push({ getters, commit }, { name, item }) {
            if (!['undos', 'redos'].includes(name)) {
                throw Error('Invalid value ' + name);
            }
            commit(name, getters[name].concat(item).slice(-UNDO_REDO_MAX_STACK));
            return getters[name].length;
        },
        clearUndoRedo({ commit }) {
            commit('undos', []);
            commit('redos', []);
            commit('currentState', null);
        },
        async placeFeature({ commit, dispatch, getters, rootGetters }, { type, preset, pageIndex, x, y, groupId, dataElementDefinitionId }) {
            let feature = Feature.build({ type, preset, x, y, groupId, dataElementDefinitionId });

            // add data element to form if necessary
            if (feature.dataElementDefinitionId) {
                let dataElement = rootGetters['dataElements/getDefinitions'].find(def => def.id === feature.dataElementDefinitionId);

                dataElement.featureId = feature.id;
                commit('addDataElement', { ...dataElement });
            }

            await dispatch('addFeature', { feature, pageIndex });
            commit('activeFeatures', [feature]);
            dispatch('finalizePositionChanges');
        },
        async addFeature({ commit, getters, dispatch }, { feature, pageIndex }) {
            // Add/update feature groups for certain feature types
            if (feature.type === 'signature') {
                if (!feature.groupId || feature.id !== getters.featureGroups[feature.groupId].masterId) {
                    let groupId = await dispatch('addFeatureGroup', { type: 'signature', masterId: feature.id });
                    commit('updateFeature', { feature, updates: { groupId } });
                }
            }

            // features with a tabindex already are just moving from another page, not being added from scratch
            if (feature.focusable && !feature.tabIndex) feature.tabIndex = getters.maxTabIndex + 10;

            commit('addFeature', { feature, pageIndex });
        },
        async deleteFeatures({ getters, commit, dispatch }, skipWarning = false) {
            let dataElements = getters.activeFeatures.filter(feature => feature.hasOwnProperty('dataElementDefinitionId'));
            let signatures = getters.activeFeatures.filter(feature => feature.type === 'signature');

            if (!skipWarning) {
                let numFeatures = getters.activeFeatures.length;
                let body = `Delete ${numFeatures} feature${numFeatures > 1 ? 's' : ''}?`;
                if (dataElements.length) {
                    let numLinkedFeatures = dataElements.reduce((acc, feature) => {
                        acc += getters.features.filter(f => f.linkedDataElementFeatureId === feature.id).length;
                        return acc;
                    }, 0);
                    if (numLinkedFeatures) {
                        body += ` Data Element links will be removed.`;
                    }
                }
                let res = await dispatch('dialog/set', {
                    type: 'confirm',
                    header: `Delete Feature${numFeatures > 1 ? 's' : ''}`,
                    body,
                    okText: 'Delete',
                    cancelText: 'Cancel',
                    critical: true
                }, { root: true });
                if (!res) return;
            }

            dataElements.forEach(async feature => await dispatch('deleteDataElement', feature.id));
            signatures.forEach(async sig => await dispatch('deleteSignature', sig));

            // Delete feature groups' associated features if necessary
            let addToActive = [];
            for (let feature of getters.activeFeatures) {
                if (!feature.groupId) continue;
                let group = getters.featureGroups[feature.groupId];
                if (feature.id !== group.masterId) continue;
                let gFeatures = getters.featuresByGroupId(feature.groupId);
                for (let gFeature of gFeatures) {
                    if (!getters.isActiveFeature(gFeature)) {
                        addToActive.push(gFeature);
                    }
                }
            }
            if (addToActive.length) {
                commit('activeFeatures', getters.activeFeatures.concat(addToActive));
            }

            commit('deleteFeatures');
            dispatch('cleanFeatureGroups');
        },
        async updateFeatures({ commit, getters, dispatch }, updates) {
            getters.activeFeatures.forEach(feature => {
                // A feature can't be both "office use only" and "lock on any signature" at once
                if (updates.officeUse && feature.lock && feature.lock[0] === 'any') {
                    updates.lock = ['never'];
                } else if (updates.lock && updates.lock[0] === 'any' && feature.officeUse) {
                    updates.officeUse = false;
                }

                // Override some settings when a feature is added to a group
                if (updates.groupId) {
                    if (feature.availableConfig.includes('checkbox')) {
                        updates.required = false;
                        updates.checked = false;
                    }
                }

                // Only one checkbox in a group may be checked by default
                if (updates.checked && feature.groupId) {
                    let gFeatures = getters.featuresByGroupId(feature.groupId, feature.id);
                    for (let gFeature of gFeatures) {
                        commit('updateFeature', { feature: gFeature, updates: { checked: false } });
                    }
                }

                commit('updateFeature', { feature, updates });

                if (updates.hasOwnProperty('tabIndex')) {
                    dispatch('incrementDuplicateTabIndexes', { feature });
                }
            });
        },
        addFeatureGroup({ commit, getters }, group) {
            let groups = cloneDeep(getters.featureGroups);
            group.color = 0;
            let colors = Object.values(groups).map(g => g.color).sort((a, b) => a - b);
            for (let color of colors) {
                if (group.color < color) break;
                group.color++;
            }
            let groupId = strings.random(6);
            group.id = groupId;
            group.name = `Group_${groupId}`;
            groups[groupId] = group;
            commit('featureGroups', groups);
            return groupId;
        },
        deleteFeatureGroup({ commit, getters }, groupId) {
            let groups = cloneDeep(getters.featureGroups);
            delete groups[groupId];
            commit('featureGroups', groups);
        },
        cleanFeatureGroups({ commit, getters, dispatch }) {
            for (let groupId in getters.featureGroups) {
                let features = getters.featuresByGroupId(groupId);
                if (
                    !features.length ||
                    (features.length === 1 && !parentFeatureTypes.includes(features[0].type))
                ) {
                    dispatch('deleteFeatureGroup', groupId);
                    if (features.length) {
                        commit('updateFeature', {
                            feature: features[0],
                            updates: { groupId: undefined }
                        });
                    }
                }
            }
        },
        async deleteDataElement({ getters, commit }, featureId) {
            // delete all links
            getters.features
                .filter(feature => feature.linkedDataElementFeatureId === featureId)
                .forEach(feature => commit('updateFeature', {
                    feature,
                    updates: {
                        'linkedDataElementFeatureId': '',
                        'linkedDataElemProp': ''
                    }
                }));

            commit('deleteDataElement', featureId);
        },
        deleteSignature({ getters, commit }, sig) {
            for (let feature of getters.features) {
                // remove 'required before' signing
                if (Array.isArray(feature.required) && feature.required.includes(sig.id)) {
                    let newRequired = feature.required.filter(r => r !== sig.id);
                    if (!newRequired.length) newRequired = ['never'];
                    commit('updateFeature', {
                        feature,
                        updates: {
                            required: newRequired
                        }
                    });
                }

                // remove 'lock after' signing
                if (Array.isArray(feature.lock) && feature.lock.includes(sig.id)) {
                    let newLock = feature.lock.filter(r => r !== sig.id);
                    if (!newLock.length) newLock = ['any'];
                    commit('updateFeature', {
                        feature,
                        updates: {
                            lock: newLock
                        }
                    });
                }
            }
        },
        incrementDuplicateTabIndexes({ commit, getters, dispatch }, { feature }) {
            let duplicateIndexFeature = getters.features.find(f => f !== feature && f.tabIndex === feature.tabIndex);
            if (duplicateIndexFeature) {
                let newTabIndex = feature.tabIndex + 1;
                commit('updateFeature', { feature: duplicateIndexFeature, updates: { tabIndex: newTabIndex } });
                dispatch('incrementDuplicateTabIndexes', { feature: duplicateIndexFeature });
            }
        },
        async pasteFeatures({ commit, getters, dispatch, rootGetters }, { pageIndex, x, y } = {}) {
            let copiedFeatures = cloneDeep(getters.copiedFeatures);

            // Calculate the dimensions of the feature group
            let minX = 99999, minY = 99999, maxX = 0, maxY = 0;
            for (let feature of copiedFeatures) {
                if (feature.x < minX) minX = feature.x;
                if (feature.y < minY) minY = feature.y;
                let right = feature.x + feature.width;
                if (right > maxX) maxX = right;
                let bottom = feature.y + feature.height;
                if (bottom > maxY) maxY = bottom;
            }

            // Use the page (and optionally coords) of the middle of the viewport
            if (pageIndex === undefined) {
                let canvas = document.querySelector('.main .canvas');
                let midPoint = canvas.scrollTop + canvas.clientHeight / 2;
                let pages = canvas.querySelectorAll('.page-wrapper');
                pageIndex = -1;
                for (let page of pages) {
                    if (page.offsetTop > midPoint) break;
                    pageIndex++;
                }
                if (x === undefined || y === undefined) {
                    let pageWidth = getters.pageMeasurementsBasic(pageIndex).width;
                    x = pageWidth / 2 - (maxX - minX) / 2;
                    let pageRect = pages[pageIndex].getBoundingClientRect();
                    let scrollY = window.innerHeight / 2 - pageRect.top;
                    y = scrollY / rootGetters['layout/scaleFactor'] - (maxY - minY) / 2;
                }
            }

            copiedFeatures.sort((a, b) => a.tabIndex > b.tabIndex ? 1 : -1);
            const newGroupIds = [];
            for (let copy of copiedFeatures) {
                copy.id = Feature.generateId();
                copy.name = getters.newFeatureName(copy);
                if (x !== undefined && y !== undefined) {
                    copy.x += x - minX;
                    copy.y += y - minY;
                }
                if (copy.tabIndex) copy.tabIndex = getters.maxTabIndex + 10;

                // add data element to form if necessary
                if (copy.dataElementDefinitionId) {
                    let dataElement = rootGetters['dataElements/getDefinitions'].find(def => def.id === copy.dataElementDefinitionId);
                    dataElement.featureId = copy.id;
                    commit('addDataElement', { ...dataElement });
                }

                // If multiple checkboxes are copied from the same feature group, add a new group for them
                if (
                    copy.groupId &&
                    !newGroupIds.includes(copy.groupId) &&
                    copy.availableConfig.includes('checkbox')
                ) {
                    const groupFeatures = copiedFeatures.filter((f) => f.groupId === copy.groupId);
                    if (groupFeatures.length > 1) {
                        const group = getters.featureGroups[copy.groupId];
                        const newGroupId = await dispatch('addFeatureGroup', { ...group });
                        groupFeatures.map((f) => f.groupId = newGroupId);
                        newGroupIds.push(newGroupId)
                    }
                }

                await dispatch('addFeature', { feature: copy, pageIndex });
            }

            commit('activeFeatures', copiedFeatures);
            dispatch('finalizePositionChanges');
        },
        activeFeaturesStacking({ getters, commit }, position) {
            if (!['up', 'down', 'top', 'bottom'].includes(position)) throw new Error('Invalid position');

            let activeFeatures = getters.activeFeatures;
            let pageIndexes = {};

            if (['down', 'top'].includes(position)) {
                for (let i = 0; i < activeFeatures.length; i++) {
                    findAndMove(activeFeatures[i]);
                }
            } else {
                for (let i = activeFeatures.length - 1; i >= 0; i--) {
                    findAndMove(activeFeatures[i]);
                }
            }

            function findAndMove(feature) {
                let pageIndex = getters.featurePageIndex(feature);
                let page = getters.page(pageIndex);
                let oldFeatureIndex = page.features.indexOf(feature);
                let newFeatureIndex;
                if (pageIndexes[pageIndex] === undefined) {
                    pageIndexes[pageIndex] = { max: page.features.length - 1, min: 0 }
                }

                if (position === 'top') {
                    newFeatureIndex = pageIndexes[pageIndex].max;
                }

                if (position === 'bottom') {
                    newFeatureIndex = pageIndexes[pageIndex].min;
                }

                if (position === 'up') {
                    newFeatureIndex = Math.min(oldFeatureIndex + 1, pageIndexes[pageIndex].max);
                    pageIndexes[pageIndex].max = newFeatureIndex - 1; // prevent elems leapfrogging past higher elems which didn't move
                }

                if (position === 'down') {
                    newFeatureIndex = Math.max(oldFeatureIndex - 1, pageIndexes[pageIndex].min);
                    pageIndexes[pageIndex].min = newFeatureIndex + 1; // prevent elems leapfrogging past lower elems which didn't move
                }

                commit('changeFeatureOrder', { pageIndex, oldFeatureIndex, newFeatureIndex })
            }
        },
        nudge({ getters, commit, dispatch }, { axis, distance }) {
            commit('incrementPendingChanges', { [axis]: distance });
            if (getters.activeFeatures.length === 1) {
                dispatch('finalizePositionChanges');
            }
        },
        finalizePositionChanges({ commit, getters, dispatch }) {
            getters.activeFeatures.forEach(feature => {
                applyPendingChanges(feature);
                enforceMinimums(feature);

                if (!isFullyContainedInPage(feature)) {
                    moveToPageIfNecessary(feature);
                    constrainInCurrentPage(feature);
                }
            });
            commit('setPendingChanges', {});

            function applyPendingChanges(feature) {
                let pending = getters.pendingChanges;
                let isEmpty = true;
                let updates = {};
                for (let key in pending) {
                    isEmpty = false;
                    updates[key] = feature[key] + pending[key];
                }
                if (isEmpty) return;
                commit('updateFeature', { feature, updates });
            }

            function isFullyContainedInPage(feature) {
                let currentPageIndex = getters.featurePageIndex(feature);
                let pageMeasurements = getters.pageMeasurementsBasic(currentPageIndex);
                return feature.x >= 0 &&
                    feature.y >= 0 &&
                    feature.x + feature.width <= pageMeasurements.width &&
                    feature.y + feature.height <= pageMeasurements.height;
            }

            function moveToPageIfNecessary(feature) {
                let currentPageIndex = getters.featurePageIndex(feature);
                let moveToIndex = currentPageIndex;
                let originalPageAbsY = getters.pageAbsY(currentPageIndex);
                let featureMiddleAbsY = originalPageAbsY + feature.y + feature.height / 2;
                let closestDistance = 100000;

                for (let i = 0; i < getters.numPages; i++) {
                    let tempPageMeasurements = getters.pageMeasurements(i);

                    let tempAbsTop = getters.pageAbsY(i);
                    let tempAbsBottom = tempAbsTop + tempPageMeasurements.height;
                    let tempDistanceFromTop = Math.abs(featureMiddleAbsY - tempAbsTop);
                    let tempDistanceFromBottom = Math.abs(featureMiddleAbsY - tempAbsBottom);
                    let tempClosestDistance = Math.min(tempDistanceFromTop, tempDistanceFromBottom);
                    if (tempClosestDistance < closestDistance) {
                        closestDistance = tempClosestDistance;
                        moveToIndex = i;
                    }
                }

                if (moveToIndex !== currentPageIndex) {
                    // move feature to different page
                    dispatch('addFeature', { feature, pageIndex: moveToIndex });
                    commit('removeFeature', { feature, pageIndex: currentPageIndex });

                    // reposition on page to correct x, y
                    let originalPageAbsX = getters.pageAbsX(currentPageIndex);
                    let pageAbsY = getters.pageAbsY(moveToIndex);
                    let pageAbsX = getters.pageAbsX(moveToIndex);
                    commit('updateFeature', {
                        feature,
                        updates: {
                            y: feature.y + originalPageAbsY - pageAbsY,
                            x: feature.x + originalPageAbsX - pageAbsX
                        }
                    });
                }
            }

            function constrainInCurrentPage(feature) {
                let currentPageIndex = getters.featurePageIndex(feature);
                let pageMeasurements = getters.pageMeasurements(currentPageIndex);

                let updates = {};
                if (feature.width > pageMeasurements.width) updates.width = pageMeasurements.width;
                if (feature.height > pageMeasurements.height) updates.height = pageMeasurements.height;
                commit('updateFeature', { feature, updates });
                
                updates = {};
                if (feature.x < 0) {
                    updates.x = 0;
                } else if (feature.x + feature.width > pageMeasurements.width) {
                    updates.x = pageMeasurements.width - feature.width;
                }
                if (feature.y < 0) {
                    updates.y = 0;
                } else if (feature.y + feature.height > pageMeasurements.height) {
                    updates.y = pageMeasurements.height - feature.height;
                }
                commit('updateFeature', { feature, updates });
            }

            function enforceMinimums(feature) {
                let minWidth = feature.minWidth || constants.FORM_PPMM;
                let minHeight = feature.minHeight || constants.FORM_PPMM;
                if (feature.width < minWidth) {
                    commit('updateFeature', { feature, updates: { width: minWidth } });
                }
                if (feature.height < minHeight) {
                    commit('updateFeature', { feature, updates: { height: minHeight } });
                }
            }
        },
        rotateActiveFeatures({ commit, getters, dispatch }, degrees) {
            // TODO actually rotate features
            // currently just swaps w->h while maintaining center pos
            getters.activeFeatures.forEach(feature => {
                let halfW = feature.width / 2;
                let halfH = feature.height / 2;
                let x = feature.x + halfW - halfH;
                let y = feature.y - halfW + halfH;

                commit('updateFeature', {
                    feature,
                    updates: {
                        width: feature.height,
                        height: feature.width,
                        x,
                        y
                    }
                });
            });

            dispatch('finalizePositionChanges');
        },
        async save({ commit, getters, dispatch }, publish) {
            let form = getters.form(0);
            let isDraft = form.is_draft;
            let sessionStarted = getters.sessionStarted;
            commit('isDraft', !publish);

            try {
                let hasPublicPackage;
                if (form.id && isDraft) {
                    let res = await api.put(`forms/${form.id}`, { form, sessionStarted });
                    commit('startSession', true);
                    hasPublicPackage = res.hasPublicPackage;
                } else {
                    let res = await api.post('forms', { path: getters.pendingPath, form, sessionStarted });
                    commit('setForms', [res.form]);
                    hasPublicPackage = res.hasPublicPackage;
                }
                commit('hasChanges', false);

                if (hasPublicPackage) {
                    let res = await dispatch('dialog/set', {
                        type: 'confirm',
                        header: 'Update Public Links',
                        body: `There are active public links for this form. Do you want to update all public links to use this new version?`,
                        okText: 'Update Public Links',
                        cancelText: "Don't Update"
                    }, { root: true });
                    if (res) {
                        await api.put(`forms/${form.id}/publicPackages`);
                    }
                }
            } catch (err) {
                commit('isDraft', isDraft);
                throw err;
            }
        },
        async load({ commit }, formIds) {
            let promises = formIds.map(async id => await api.get(`forms/${id}`, { drafts: 1 }));
            let forms = await Promise.all(promises);
            commit('setForms', forms);
        },
        async discard({ commit, getters }) {
            const form = getters.form(0);
            if (form.id && form.is_draft) {
                await api.delete(`forms/${form.id}/draft`);
            }
            commit('resetForms');
        },
        print({ dispatch }, opts = null) {
            if (!opts) opts = {};
            opts.download = false;
            dispatch('openPdf', opts);
        },
        download({ dispatch }, opts = null) {
            if (!opts) opts = {};
            opts.download = true;
            dispatch('openPdf', opts);
        },
        async openPdf({ getters, dispatch, rootGetters }, opts) {
            let formId = opts.formId;
            let drafts = opts.drafts ? 1 : 0;

            let loadedForm;
            if (!formId) {
                loadedForm = getters.form(0);
                formId = loadedForm.id;
                drafts = loadedForm.is_draft ? 1 : 0;
                if (getters.hasChanges || /^new/i.test(formId)) {
                    dispatch('notification/set', {
                        message: `This form's changes must be saved before it can be ${opts.download ? 'downloaded' : 'printed'}`,
                        type: 'info'
                    }, { root: true });
                    return;
                }
            }

            // Circumvent the browser popup blocker by synchronously creating a new tab
            let tab = window.open('', 'paperwork_pdf' + strings.random(4));

            // If the current form has an existing PDF, no need to call API
            if (loadedForm && loadedForm.pdf_url) {
                tab.location.href = loadedForm.pdf_url;
                return;
            }

            let res;
            try {
                res = await api.get(`forms/${formId}/pdf?drafts=${drafts}`);
            } catch (err) {
                tab.close();
                throw err;
            }
            if (tab.closed) return;
            if (res.pending) {
                let query = api.queryString({ fid: formId, drafts });
                tab.location.href = `${window.location.origin}/pdf${query}`;
            } else {
                tab.location.href = res.url;
            }
        },
        async activeFeatures({ commit, dispatch }, features) {
            dispatch('finalizePositionChanges');
            await timeout(); // Let change events fire before clearing activeFeatures
            commit('activeFeatures', features);
        },
        async toggleActiveFeature({ commit, dispatch }, feature) {
            dispatch('finalizePositionChanges');
            await timeout(); // Let change events fire before clearing activeFeatures
            commit('toggleActiveFeature', feature);
        },
        async exportCsv({ rootGetters }, formId) {
            const name = strings.sanitizeFilename(rootGetters['library/forms'][formId]?.name || "Form");
            const date = dateFormat('yyyy-mm-dd');
            const filename = `${name}_Submissions_${date}.csv`;
            await api.download(`forms/${formId}/csv`, null, { filename });
        },
        deletePage({ commit, dispatch }, index) {
            commit('deletePage', index);
            dispatch('cleanFeatureGroups');
        },
    }
}
